import { FieldArray, Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import ConfirmSelect from '../../../components/confirm-select';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadProgram, canWriteProgram, compareArrayObjectValue,
  getErrorObject, getProgramOtherAttributes, isFormDataValid, isFormReferenceValid,
  isNullOrWhiteSpace
} from '../../../utils/helper';
import { AddIcon, DeleteIcon } from '../../../utils/icons';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, IconButton, Input, InputAdornment,
  InputLabel, Link, Menu, MenuItem, Select, Switch, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

interface tmCRUDProps {
  open: boolean;
  programKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value

const formDefaultValue = {
  metadata: {
    programBillingMethod: "",
    passShipToPhoneToShopify: false,
    subscribeToInstallationAlert: false,
    whiskerGroupId: "",
    billableFolderId: -1,
    whiskerGroupType: "",
  },
  theme: {
    clientCompanyPath: "",
    clientCompanyURL: "",
    companyName: "",
    enlistmentFavicon: "",
    enlistmentMainPageBackgroundImage: "",
    phoneNumber: "",
    preEnrollmentSupportEmail: "",
    preEnrollmentSupportPhone: "",
    programPath: "",
    locationType:"",
    supportLevel:"",
    companyLogo: "",
    defaultSchemas: [""],
    deviceLocations: [""],
    emailURL: "",
    logoUrl: "",
    participantionAgreementUrl: "",
    primaryColor: "",
    primaryTextColor: "",
    secondaryColor: "",
    secondaryTextColor: "",
    sensorStoreLink: false,
    shopifyDiscountCode: "",
    showLogo: false,
    supportNumber: "",
    warranties: [
      {
        warrantyNumber: "",
        warrantyType: "",
      },
    ],
    waveColor: "",
    waveOpacity: 0.0,
  },
  publicPaths: [""],
  privatePaths: [""],
};

const formDefaultTouchValue = {};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  metadata: Yup.object().shape({
    programBillingMethod: Yup.string().trim().required("Additional equipment billing method is required."),
    billableFolderId: Yup.number().required("Billable folder id is required.").moreThan(-1, "Billable folder id is required.")

  }),
  theme: Yup.object().shape({
    companyName: Yup.string().trim().required("Company name is required."),
    clientCompanyPath: Yup.string().trim().required("Client company path is required."),
    programPath: Yup.string().trim().required("Program path is required."),
    locationType: Yup.string().trim().required("Location type is required."),
    supportLevel: Yup.string().trim().required("Support level is required."),
    preEnrollmentSupportEmail: Yup.string().trim().email("Support email is not valid."),
    primaryColor: Yup.string()
      .trim()
      .matches(/^#([0-9a-f]{6})$/i, "Primary background color is not valid (Ex:#FF0000)."),
    primaryTextColor: Yup.string()
      .trim()
      .matches(/^#([0-9a-f]{6})$/i, "Primary text color is not valid (Ex:#FFFFFF)."),
    secondaryColor: Yup.string()
      .trim()
      .matches(/^#([0-9a-f]{6})$/i, "Secondary background color is not valid (Ex:#FF0000)."),
    secondaryTextColor: Yup.string()
      .trim()
      .matches(/^#([0-9a-f]{6})$/i, "Secondary text color is not valid (Ex:#FFFFFF)."),
    waveColor: Yup.string()
      .trim()
      .matches(/^#([0-9a-f]{6})$/i, "Wave color is not valid (Ex:#FF0000)."),
    waveOpacity: Yup.number().positive().min(0, "Wave opacity ranges between 0 and 1").max(1.0, "Wave opacity ranges between 0 and 1"),
    warranties: Yup.array().of(
      Yup.object().shape({
        warrantyNumber: Yup.string().trim().required(" value is required."),
      })
    ),
  }),
});

const ThemeAndMetadataCRUD = ({ open, programKey, onCallback }: tmCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { deviceLocationGroup, refreshData, showAlert, userFunctionAccess, referenceLookups, appSetting } = useContext(AppContext);

  const [anchorElForWarranty, setAnchorElForWarranty] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<ProgramOtherAttribute>(formDefaultValue);
  const [deviceLocations, setDeviceLocations] = useState<Lookup[]>([]);
  const [deviceLocationSelectDialogOpen, setDeviceLocationSelectDialogOpen] = useState<boolean>(false);
  const [missingNotificationSchemas, setMissingNotificationSchemas] = useState<string[]>([]);
  const [notificationSchemas, setNotificationSchemas] = useState<Lookup[]>([]);
  const [notificationSchemaSelectDialogOpen, setNotificationSchemaSelectDialogOpen] = useState<boolean>(false);

  // Warranty

  const handleAnchorElForWarrantyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForWarranty(event.currentTarget);
  };

  const handleAnchorElForWarrantyClose = () => {
    setAnchorElForWarranty(null);
  };

  const handleAddWarrantyClick = (warrantyType: string) => {
    const warranties = formReference.current.values.theme.warranties;
    warranties.push({ warrantyType: warrantyType, warrantyNumber: "" });
    formReference.current.setFieldValue("theme.warranties", warranties);
    setSaveButtonVisible(true);
  };

  const handleDeleteWarrantyClick = (index: number) => {
    const warranties = formReference.current.values.theme.warranties;
    warranties.splice(index, 1);
    formReference.current.setFieldValue("theme.warranties", warranties);
    setSaveButtonVisible(true);
  };

  // Notification Schemas

  const selectedNotificationSchemaResult = (confirmOutput: ConfirmOutput) => {
    setNotificationSchemaSelectDialogOpen(false);

    if (confirmOutput.status === "select") {
      const tempnotificationSchemas: string[] = formReference.current.values.theme.defaultSchemas;

      confirmOutput.data.forEach((item) => {
        tempnotificationSchemas.push(item.value);
      });
      formReference.current.setFieldValue("theme.defaultSchemas", tempnotificationSchemas.sort());
      setSaveButtonVisible(true);
    }

    setNotificationSchemas([]);

    setTimeout(() => {
      checkNotificationSchemaMissing();
    }, 500);

    setTimeout(() => {
      checkOtherValidations();
    }, 1000);
  };

  const handleDeleteSelectedDefaultSchemaClick = (defaultSchemaName: string) => {
    const filterDefaultSchemas = formReference.current.values.theme.defaultSchemas.filter((value) => value !== defaultSchemaName);
    formReference.current.setFieldValue("theme.defaultSchemas", filterDefaultSchemas);
    setSaveButtonVisible(true);
    setTimeout(() => {
      checkNotificationSchemaMissing();
    }, 500);

    setTimeout(() => {
      checkOtherValidations();
    }, 2000);
  };

  const checkNotificationSchemaMissing = () => {
    if (!isFormReferenceValid(formReference)) {
      return;
    }

    setMissingNotificationSchemas([]);
    const tempMissingNotificationSchemas: string[] = [];
    formReference.current.values.theme.deviceLocations.forEach((deviceLocation) => {
      const filterItems = referenceLookups.deviceLocationNotificationSchemaValidations.filter((deviceLocationNotificationSchemaValidation) => deviceLocationNotificationSchemaValidation.deviceLocationName.toLowerCase() === deviceLocation.toLowerCase());
      if (filterItems.length > 0) {
        filterItems.forEach((filterItem) => {
          const defaultSchema = formReference.current.values.theme.defaultSchemas.find((defaultSchema) => defaultSchema.toLowerCase() === filterItem.notificationSchemaName.toLowerCase());

          if (isNullOrWhiteSpace(defaultSchema) && tempMissingNotificationSchemas.join(",").toLowerCase().indexOf(filterItem.notificationSchemaName.toLowerCase()) < 0) {
            const tempNotificationSchema = referenceLookups.notificationSchemas.find((notificationSchema) => notificationSchema.name.toLowerCase() === filterItem.notificationSchemaName.toLowerCase());

            if (tempNotificationSchema !== undefined) {
              tempMissingNotificationSchemas.push(tempNotificationSchema?.name);
            }
          }
        });
      }
    });

    if (tempMissingNotificationSchemas.length > 0) {
      setMissingNotificationSchemas(tempMissingNotificationSchemas);
      return true;
    }
    return false;
  };

  const handleAddNotificationSchemaClick = () => {
    const lookupValues: Lookup[] = [];

    referenceLookups.notificationSchemas.forEach((notificationSchema) => {
      const checked = formReference.current.values.theme.defaultSchemas.filter((defaultSchema) => defaultSchema.toLowerCase() === notificationSchema.name.toLowerCase())?.length > 0 ? true : false;
      if (!checked) {
        lookupValues.push({
          value: notificationSchema.name,
          name: notificationSchema.name,
          checked: checked,
        });
      }
    });

    formReference.current.values.theme.deviceLocations.forEach((deviceLocation) => {
      const filterItems = referenceLookups.deviceLocationNotificationSchemaValidations.filter((deviceLocationNotificationSchemaValidation) => deviceLocationNotificationSchemaValidation.deviceLocationName.toLowerCase() === deviceLocation.toLowerCase());
      if (filterItems.length > 0) {
        filterItems.forEach((filterItem) => {
          lookupValues
            .filter((item) => item.value.toLowerCase() === filterItem.notificationSchemaName.toLowerCase())
            .forEach((item) => {
              item.checked = true;
              if (filterItem.required) {
                item.name = item.name.indexOf(" - Required") <= 0 ? item.name + " - Required" : item.name;
              } else {
                item.name = item.name.indexOf(" - Recommended") <= 0 ? item.name + " - Recommended" : item.name;
              }
            });
        });
      }
    });

    setNotificationSchemas(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setNotificationSchemaSelectDialogOpen(true);
  };

  // Device Locations

  const selectedDeviceLocationResult = (confirmOutput: ConfirmOutput) => {
    setDeviceLocationSelectDialogOpen(false);

    if (confirmOutput.status === "select") {
      const tempDeviceLocations: string[] = formReference.current.values.theme.deviceLocations;

      confirmOutput.data.forEach((item) => {
        tempDeviceLocations.push(item.value);
      });
      formReference.current.setFieldValue("theme.deviceLocations", tempDeviceLocations.sort());
      setSaveButtonVisible(true);
    }

    setDeviceLocations([]);

    setTimeout(() => {
      const confirm = checkNotificationSchemaMissing();
      if (confirm && confirmOutput.status === "select") {
        handleAddNotificationSchemaClick();
      }
    }, 500);

    setTimeout(() => {
      checkOtherValidations();
    }, 2000);
  };

  const handleDeleteSelectedDeviceLocationClick = (deviceLocationName: string) => {
    const filterDeviceLocations = formReference.current.values.theme.deviceLocations.filter((value) => value !== deviceLocationName);
    formReference.current.setFieldValue("theme.deviceLocations", filterDeviceLocations);
    setSaveButtonVisible(true);
    setTimeout(() => {
      checkNotificationSchemaMissing();
    }, 500);

    setTimeout(() => {
      checkOtherValidations();
    }, 2000);
  };

  const handleAddDeviceLocationClick = () => {
    const lookupValues: Lookup[] = [];
    deviceLocationGroup.forEach((group) => {
      group.deviceLocations.forEach((deviceLocation) => {
        const checked = formReference.current.values.theme.deviceLocations.filter((devicLocation) => devicLocation === deviceLocation.name)?.length > 0 ? true : false;
        if (!checked) {
          lookupValues.push({
            value: deviceLocation.name,
            name: deviceLocation.name,
            checked: false,
          });
        }
      });
    });
    setDeviceLocations(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setDeviceLocationSelectDialogOpen(true);

    setTimeout(() => {
      checkOtherValidations();
    }, 1000);
  };

  // Form

  const checkOtherValidations = () => {
    let hasError = false;
    //const whiskerGroupId = Number(formReference.current.values.metadata.whiskerGroupId);

    let whiskerGroupId;
if (formReference.current && formReference.current.values && formReference.current.values.metadata) {
  whiskerGroupId = Number(formReference.current.values.metadata.whiskerGroupId);
}
else {
  formReference.current.values.metadata.whiskerGroupType = "";
}

    if (formReference.current.values.metadata.whiskerGroupType !== "" &&
      formReference.current.values.metadata.whiskerGroupId === "") {
      formReference.current.setFieldError("metadata.whiskerGroupId", "Whisker group is required.");
    }

    if (formReference.current.values.metadata.whiskerGroupId !== "") {
      if (isNaN(whiskerGroupId)) {
        formReference.current.setFieldError("metadata.whiskerGroupId", "Whisker group id must be number.");
        hasError = true;
      } else if (whiskerGroupId <= 0) {
        formReference.current.setFieldError("metadata.whiskerGroupId", "Whisker group id must be greater than zero.");
        hasError = true;
      }
    }

    if (formReference.current.values.metadata.whiskerGroupId !== "") {
      if (formReference.current.values.theme.deviceLocations.length > 0) {
        formReference.current.setFieldError("theme.deviceLocations", "Whiskerlab device does not need any device locations. Please remove all device locations.");
        hasError = true;
      }

      if (formReference.current.values.theme.defaultSchemas.length > 0) {
        formReference.current.setFieldError("theme.defaultSchemas", "Whiskerlab device does not need any notification schemas. Please remove all notification schemas.");
        hasError = true;
      }
    } else {
      if (formReference.current.values.theme.deviceLocations.length === 0) {
        formReference.current.setFieldError("theme.deviceLocations", "Must have at least 1 device location.");
        hasError = true;
      }

      if (formReference.current.values.theme.defaultSchemas.length === 0) {
        formReference.current.setFieldError("theme.defaultSchemas", "Must have at least 1 notification schema.");
        hasError = true;
      }
    }

    if (formReference.current.values.theme.waveColor !== "" && formReference.current.values.theme.waveOpacity === "") {
      formReference.current.setFieldError("theme.waveOpacity", "Wave opacity is required.");
      hasError = true;
    }

    if (formReference.current.values.theme.waveColor === "" && formReference.current.values.theme.waveOpacity !== "") {
      formReference.current.setFieldError("theme.waveColor", "Wave color is required.");
      hasError = true;
    }

    return hasError;
  };

  const saveProgramThemeAndMetadata = async () => {
    if (!canWriteProgram(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    if (checkOtherValidations()) {
      scrollToFieldError(formReference);
      return false;
    }

    const payload = {
      theme: formReference.current.values.theme,
      metadata: formReference.current.values.metadata,
    };

    try {
      setSaveInProgress(true);
      setReadonly(true);
      await API.post("/api/onboarding/programs/" + programKey + "/other/attributes", payload)
        .then(async (response) => {
          setLoading(true);
          refreshData("programs");
          showAlert({ message: "Successfully saved.", severity: "success" });
          onCallback({ status: "refresh", data: "" });
        })
        .catch((error) => {
          setSaveInProgress(false);
          setReadonly(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "attributes.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save theme & metadata data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      setReadonly(false);
      showAlert({
        message: apiRequestError(e, "Something was wrong in our system."),
        severity: "error",
      });
    }
  };

  const cancelProgramThemeAndMetadata = () => {
    formReference.current.resetForm({});
    onCallback({ status: "cancel", data: "" });
  };

  // Other
  const cancel = () => {
    onCallback({ status: "cancel", data: "" });
  };

  const scrollToFieldError = (formRef: any) => {
    if (readonly) {
      return;
    }

    let returnValue = false;
    if (!formRef.current.isValid) {
      try {
        Object.keys(formRef.current.errors).forEach((key) => {
          let element = document.querySelector(`input[name='${key}']`);

          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            returnValue = true;
            return;
          } else {
            element = document.querySelector(`select[name='${key}']`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              returnValue = true;
              return;
            }
          }
          if (!element) {
            Object.keys(formRef.current.errors[key]).forEach((key2) => {
              element = document.querySelector(`input[name='${key2}']`);
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                returnValue = true;
                return;
              } else {
                element = document.querySelector(`select[name='${key2}']`);
                if (element) {
                  element.scrollIntoView({ behavior: "smooth", block: "center" });
                  returnValue = true;
                  return;
                } else {
                  element = document.getElementById(`${key2}`);
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                    returnValue = true;
                    return;
                  }
                }
              }
            });
          }
        });
      } catch (e) { }

      showAlert({
        message: "One or more validation errors occurred.",
        severity: "error",
      });
    }
    return returnValue;
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setLoading(true);
        setReadonly(true);
        setSaveButtonVisible(false);

        if (isFormDataValid(formReference)) {
          formReference.current.resetForm({});
        }

        if (!canReadProgram(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (programKey === "") {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        } else {
          const apiResponse = await getProgramOtherAttributes(programKey);
          if (apiResponse.success) {
            const data = apiResponse.data;

            if (data.programOtherAttribute.theme.waveOpacity === null) {
              data.programOtherAttribute.theme.waveOpacity = "";
            }

            setInitialFormValues(data.programOtherAttribute as ProgramOtherAttribute);
            setReadonly(!canWriteProgram(userFunctionAccess));
            setLoading(false);

            setTimeout(() => {
              checkNotificationSchemaMissing();
            }, 1000);

            setTimeout(() => {
              checkOtherValidations();
            }, 3000);
          } else {
            showAlert({ message: apiResponse.errorMessage, severity: "error" });
            cancel();
            return;
          }
        }
      }
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, programKey]);

  return (
    <>
      <ConfirmSelect title="Select Device Locations" label={"Locations"} content={``} data={deviceLocations} yesText="ADD" noText="CANCEL" open={deviceLocationSelectDialogOpen} multiSelect={true} maxSelect={0} callbackOnSelect={selectedDeviceLocationResult} />

      <ConfirmSelect title="Select Default Notification Schemas" label={"Schemas"} content={``} data={notificationSchemas} yesText="ADD" noText="CANCEL" open={notificationSchemaSelectDialogOpen} multiSelect={true} maxSelect={0} callbackOnSelect={selectedNotificationSchemaResult} />

      <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"md"}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View Theme & Metadata" : "Edit  Theme & Metadata"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <React.Fragment>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              <Box className={pageStyles.contentText}>
                <Formik
                  innerRef={formReference}
                  initialValues={initialFormValues}
                  validationSchema={formValidationSchema}
                  validateOnMount={true}
                  initialTouched={formDefaultTouchValue}
                  validateOnBlur={true}
                  validateOnChange={true}
                  onSubmit={(values: ProgramOtherAttribute) => {
                    saveProgramThemeAndMetadata();
                  }}
                >
                  {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, setFieldError, resetForm }) => (
                    <Form
                      onChange={() => {
                        setSaveButtonVisible(true);
                        setTimeout(() => {
                          checkOtherValidations();
                        }, 3000);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          checkOtherValidations();
                        }, 500);
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Metadata</Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Pass Shipping Phone To Shopify
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel control={<Switch disabled={readonly} id="metadata.passShipToPhoneToShopify" name="metadata.passShipToPhoneToShopify" checked={values.metadata.passShipToPhoneToShopify} onChange={handleChange} />} label={values.metadata.passShipToPhoneToShopify ? "Yes" : "No"} labelPlacement="end" />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <FormControl className={globalStyles.formControl} fullWidth={true} disabled={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Subscribe To Installation Alert
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel control={<Switch disabled={readonly} id="metadata.subscribeToInstallationAlert" name="metadata.subscribeToInstallationAlert" checked={values.metadata.subscribeToInstallationAlert} onChange={handleChange} />} label={values.metadata.subscribeToInstallationAlert ? "Yes" : "No"} labelPlacement="end" disabled={true} />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={8}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="programBillingMethod-label" shrink={true}>
                              Additional Equipment Billing Method
                            </InputLabel>
                            <Select native labelId="programBillingMethod-label" id="metadata.programBillingMethod" name="metadata.programBillingMethod" value={values.metadata.programBillingMethod} onChange={handleChange} label="Additional Equipment Billing Method" error={Boolean(getIn(errors, "metadata.programBillingMethod"))}>
                              <option value={""}></option>
                              {referenceLookups.programBillingMethodTypes.map((programBillingMethodType, index) => (
                                <option value={programBillingMethodType.type} key={index}>
                                  {programBillingMethodType.name}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "metadata.programBillingMethod")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="billable-folder-id-label" shrink={true}>
                              Billable Folder Id
                            </InputLabel>
                            <Select native labelId="billable-folder-id-label" id="metadata.billableFolderId" name="metadata.billableFolderId" value={values.metadata.billableFolderId} onChange={handleChange} label="Billable Folder Id" error={Boolean(getIn(errors, "metadata.billableFolderId"))}>
                              <option value={-1}></option>
                              {referenceLookups.billableFolderTypes.map((billableFolderType, index) => (
                                <option value={billableFolderType.value} key={index}>
                                  {billableFolderType.text}
                                </option>
                              ))}
                            </Select>
                            <FormHelperText error>{getIn(errors, "metadata.billableFolderId")}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="whiskerGroupType-label" shrink={true}>
                              Whisker Group Type
                            </InputLabel>
                            <Select native labelId="whiskerGroupType-label" id="metadata.whiskerGroupType" name="metadata.whiskerGroupType" value={values.metadata.whiskerGroupType} onChange={handleChange} label="Whisker Group Type" error={Boolean(getIn(errors, "metadata.whiskerGroupType"))}>
                              <option value={""}></option>
                              <option value={"Direct"}>Direct</option>
                              <option value={"Indirect"}>Indirect</option>
                            </Select>
                            <FormHelperText error>{getIn(errors, "metadata.whiskerGroupType")}</FormHelperText>
                          </FormControl>
                        </Grid>
                        {
                          values.metadata.whiskerGroupType !== "" ?

                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                disabled={readonly}
                                id="metadata.whiskerGroupId"
                                name="metadata.whiskerGroupId"
                                label="Whisker Group Id"
                                value={values.metadata.whiskerGroupId}
                                onBlur={handleBlur}
                                error={Boolean(getIn(errors, "metadata.whiskerGroupId"))}
                                helperText={getIn(errors, "metadata.whiskerGroupId")}
                                onChange={(e) => {
                                  handleChange(e);
                                  setTimeout(() => {
                                    checkOtherValidations();
                                  }, 1000);
                                }}
                              />
                            </Grid>
                            : null
                        }

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Theme</Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField disabled={readonly} id="theme.companyName" name="theme.companyName" label="Company Name" value={values.theme.companyName} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.companyName"))} helperText={getIn(errors, "theme.companyName")} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField type={"url"} disabled={readonly} id="theme.clientCompanyURL" name="theme.clientCompanyURL" label="Client Company URL" value={values.theme.clientCompanyURL} onChange={handleChange} onBlur={handleBlur} />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField disabled={readonly} id="theme.clientCompanyPath" name="theme.clientCompanyPath" label="Client Company Path" value={values.theme.clientCompanyPath} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.clientCompanyPath"))} helperText={getIn(errors, "theme.clientCompanyPath")} />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField disabled={readonly} id="theme.programPath" name="theme.programPath" label="Program Path" value={values.theme.programPath} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.programPath"))} helperText={getIn(errors, "theme.programPath")} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "theme.locationType"))}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Location Type
                            </InputLabel>
                            <Select native labelId="platform-vendor-id-label" id="theme.locationType" name="theme.locationType" value={values.theme.locationType} onChange={handleChange} label="Location Type">
                              <option value={""}></option> 
                              {referenceLookups.locationtypes.map((locationType, index) => (
                                <option value={locationType.text} key={index}>
                                  {locationType.text}
                                </option>
                              ))}                           
                            </Select>  
                            <FormHelperText error>{errors?.theme?.locationType}</FormHelperText>                          
                          </FormControl>                          
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "theme.supportLevel"))}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Support Level
                            </InputLabel>
                            <Select native labelId="platform-vendor-id-label" id="theme.supportLevel" name="theme.supportLevel" value={values.theme.supportLevel} onChange={handleChange} label="Support Level">
                              <option value={""}></option> 
                              {referenceLookups.supportLevel.map((supportLevel, index) => (
                                <option value={supportLevel.text} key={index}>
                                  {supportLevel.text}
                                </option>
                              ))}                             
                            </Select>
                            <FormHelperText error>{errors?.theme?.supportLevel}</FormHelperText>                          
                          </FormControl>
                        </Grid>
                        <Grid item xs={10}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Company Logo
                            </InputLabel>
                            <Select native labelId="platform-vendor-id-label" id="theme.companyLogo" name="theme.companyLogo" value={values.theme.companyLogo} onChange={handleChange} label="Company Logo">
                              <option value={""}></option>
                              {values.publicPaths.map((path, index) => (
                                <option value={path} key={index}>
                                  {path}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          {values.theme.companyLogo !== "" ? (
                            <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                              <img src={`${appSetting.meshifyUrl}/api/files/public${values.theme.companyLogo}`} style={{ width: "60px", height: "auto" }} alt="Company Logo" />
                            </Box>
                          ) : null}
                        </Grid>

                        <Grid item xs={10}>
                          <TextField disabled={readonly} id="theme.logoUrl" name="theme.logoUrl" label="Company Logo URL" value={values.theme.logoUrl} onChange={handleChange} onBlur={handleBlur} />
                        </Grid>
                        <Grid item xs={2}>
                          <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                            {values.theme.logoUrl !== "" ? (
                              <Button variant="outlined" color="secondary">
                                <Link href={values.theme.logoUrl} target="_blank" rel="noopener">
                                  Open
                                </Link>
                              </Button>
                            ) : null}
                          </Box>
                        </Grid>

                        <Grid item xs={10}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Participantion Agreement Url
                            </InputLabel>
                            <Select native labelId="participantion-agreement-url-label" id="theme.participantionAgreementUrl" name="theme.participantionAgreementUrl" value={values.theme.participantionAgreementUrl.toLowerCase()} onChange={handleChange} label="Participantion Agreement Url">
                              <option value={""}></option>
                              {values.privatePaths.map((path, index) => (
                                <option value={path.toLowerCase()} key={index}>
                                  {path}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                            {values.theme.participantionAgreementUrl !== "" ? (
                              <Button variant="outlined" color="secondary">
                                <Link href={`${appSetting.meshifyUrl}/api/files/private${values.theme.participantionAgreementUrl}`} target="_blank" rel="noopener">
                                  Open
                                </Link>
                              </Button>
                            ) : null}
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField disabled={true} id="theme.emailURL" name="theme.emailURL" label="Email URL" value={values.theme.emailURL} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.emailURL"))} helperText={getIn(errors, "theme.emailURL")} />
                        </Grid>

                        <Grid item xs={6} md={4}>
                          <TextField disabled={readonly} id="theme.phoneNumber" name="theme.phoneNumber" label="Phone Number (Enrollment)" value={values.theme.phoneNumber} onChange={handleChange} onBlur={handleBlur} />
                        </Grid>

                        <Grid item xs={6} md={4}>
                          <TextField disabled={readonly} id="theme.supportNumber" name="theme.supportNumber" label="Phone Number (Reactions)" value={values.theme.supportNumber} onChange={handleChange} onBlur={handleBlur} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField disabled={readonly} id="theme.shopifyDiscountCode" name="theme.shopifyDiscountCode" label="Shopify Discount Code" value={values.theme.shopifyDiscountCode} onChange={handleChange} onBlur={handleBlur} />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Show Logo
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel control={<Switch disabled={readonly} id="theme.showLogo" name="theme.showLogo" checked={values.theme.showLogo} onChange={handleChange} />} label={values.theme.showLogo ? "Yes" : "No"} labelPlacement="end" />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl className={globalStyles.formControl} fullWidth={true}>
                            <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                              Show Sensor Store Link
                            </FormLabel>
                            <FormGroup>
                              <FormControlLabel control={<Switch disabled={readonly} id="theme.sensorStoreLink" name="theme.sensorStoreLink" checked={values.theme.sensorStoreLink} onChange={handleChange} />} label={values.theme.sensorStoreLink ? "Yes" : "No"} labelPlacement="end" />
                            </FormGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={7}>
                              <Grid container spacing={3}>
                                <Grid item xs={9}>
                                  <TextField
                                    disabled={readonly}
                                    id="theme.waveColor"
                                    name="theme.waveColor"
                                    label="Wave Color"
                                    value={values.theme.waveColor}
                                    onChange={(e) => {
                                      handleChange(e);
                                      setTimeout(() => {
                                        checkOtherValidations();
                                      }, 1000);
                                    }}
                                    onBlur={handleBlur}
                                    error={Boolean(getIn(errors, "theme.waveColor"))}
                                    helperText={getIn(errors, "theme.waveColor")}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField type={"color"} disabled={readonly} id="theme.waveColor" name="theme.waveColor" label=" " value={values.theme.waveColor} onChange={handleChange} onBlur={handleBlur} className={pageStyles.colorTextBoxRoot} />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <Box p={3} display="flex" justifyContent="center" flexDirection="column">
                                {values.theme.waveColor !== "" && values.theme.waveColor !== "" && !Boolean(getIn(errors, "theme.waveColor")) && !Boolean(getIn(errors, "theme.waveColor")) ? (
                                  <>
                                    <Box
                                      p={1}
                                      display="flex"
                                      justifyContent="center"
                                      style={
                                        {
                                          // backgroundColor: `${values.theme.waveColor}`,
                                        }
                                      }
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
                                        <path
                                          fill={values.theme.waveColor}
                                          fill-opacity="1"
                                          d="M0,320L26.7,320C53.3,320,107,320,160,272C213.3,224,267,128,320,90.7C373.3,53,427,75,480,74.7C533.3,75,587,53,640,42.7C693.3,32,747,32,800,53.3C853.3,75,907,117,960,144C1013.3,171,1067,181,1120,160C1173.3,139,1227,85,1280,74.7C1333.3,64,1387,96,1413,112L1440,128L1440,320L1413.3,320C1386.7,320,1333,320,1280,320C1226.7,320,1173,320,1120,320C1066.7,320,1013,320,960,320C906.7,320,853,320,800,320C746.7,320,693,320,640,320C586.7,320,533,320,480,320C426.7,320,373,320,320,320C266.7,320,213,320,160,320C106.7,320,53,320,27,320L0,320Z"
                                          style={{
                                            opacity: `${values.theme.waveOpacity}`,
                                          }}
                                        ></path>
                                      </svg>
                                      <Typography variant="caption">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Typography>
                                    </Box>
                                  </>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            disabled={readonly}
                            multiline={false}
                            id="theme.waveOpacity"
                            name="theme.waveOpacity"
                            label="Wave Opacity"
                            value={values.theme.waveOpacity}
                            type="number"
                            inputProps={{
                              step: 0.1,
                            }}
                            onChange={(e) => {
                              handleChange(e);
                              setTimeout(() => {
                                checkOtherValidations();
                              }, 1000);
                            }}
                            onBlur={handleBlur}
                            error={Boolean(getIn(errors, "theme.waveOpacity"))}
                            helperText={getIn(errors, "theme.waveOpacity")}
                          />
                        </Grid>

                        {/* Color */}
                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Colors</Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={8}>
                              <Grid container spacing={3}>
                                <Grid item xs={9} sm={10}>
                                  <TextField disabled={readonly} id="theme.primaryColor" name="theme.primaryColor" label="Primary Background Color" value={values.theme.primaryColor} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.primaryColor"))} helperText={getIn(errors, "theme.primaryColor")} />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                  <TextField type={"color"} disabled={readonly} id="theme.primaryColor" name="theme.primaryColor" label=" " value={values.theme.primaryColor} onChange={handleChange} onBlur={handleBlur} className={pageStyles.colorTextBoxRoot} />
                                </Grid>

                                <Grid item xs={9} sm={10}>
                                  <TextField disabled={readonly} id="theme.primaryTextColor" name="theme.primaryTextColor" label="Primary Text Color" value={values.theme.primaryTextColor} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.primaryTextColor"))} helperText={getIn(errors, "theme.primaryTextColor")} />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                  <TextField type={"color"} disabled={readonly} id="theme.primaryTextColor" name="theme.primaryTextColor" label=" " value={values.theme.primaryTextColor} onChange={handleChange} onBlur={handleBlur} className={pageStyles.colorTextBoxRoot} />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box p={3} display="flex" justifyContent="center" flexDirection="column">
                                {values.theme.primaryColor !== "" && values.theme.primaryTextColor !== "" && !Boolean(getIn(errors, "theme.primaryColor")) && !Boolean(getIn(errors, "theme.primaryTextColor")) ? (
                                  <>
                                    <Box
                                      p={1}
                                      display="flex"
                                      justifyContent="center"
                                      style={{
                                        backgroundColor: `${values.theme.primaryColor}`,
                                        color: `${values.theme.primaryTextColor}`,
                                      }}
                                    >
                                      <Typography variant="caption">Sample</Typography>
                                    </Box>
                                    <Box
                                      p={1}
                                      display="flex"
                                      justifyContent="center"
                                      style={{
                                        backgroundColor: `${values.theme.primaryTextColor}`,
                                        color: `${values.theme.primaryColor}`,
                                      }}
                                    >
                                      <Typography variant="caption">Sample</Typography>
                                    </Box>
                                  </>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={8}>
                              <Grid container spacing={3}>
                                <Grid item xs={9} sm={10}>
                                  <TextField disabled={readonly} id="theme.secondaryColor" name="theme.secondaryColor" label="Secondary Background Color" value={values.theme.secondaryColor} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.secondaryColor"))} helperText={getIn(errors, "theme.secondaryColor")} />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                  <TextField type={"color"} disabled={readonly} id="theme.secondaryColor" name="theme.secondaryColor" label=" " value={values.theme.secondaryColor} onChange={handleChange} onBlur={handleBlur} className={pageStyles.colorTextBoxRoot} />
                                </Grid>
                                <Grid item xs={9} sm={10}>
                                  <TextField disabled={readonly} id="theme.secondaryTextColor" name="theme.secondaryTextColor" label="Secondary Text Color" value={values.theme.secondaryTextColor} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.secondaryTextColor"))} helperText={getIn(errors, "theme.secondaryTextColor")} />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                  <TextField type={"color"} disabled={readonly} id="theme.secondaryTextColor" name="theme.secondaryTextColor" label=" " value={values.theme.secondaryTextColor} onChange={handleChange} onBlur={handleBlur} className={pageStyles.colorTextBoxRoot} />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Box p={3} display="flex" justifyContent="center" flexDirection="column">
                                {values.theme.secondaryColor !== "" && values.theme.secondaryTextColor !== "" && !Boolean(getIn(errors, "theme.secondaryColor")) && !Boolean(getIn(errors, "theme.secondaryTextColor")) ? (
                                  <>
                                    <Box
                                      p={1}
                                      display="flex"
                                      justifyContent="center"
                                      style={{
                                        backgroundColor: `${values.theme.secondaryColor}`,
                                        color: `${values.theme.secondaryTextColor}`,
                                      }}
                                    >
                                      <Typography variant="caption">Sample</Typography>
                                    </Box>
                                    <Box
                                      p={1}
                                      display="flex"
                                      justifyContent="center"
                                      style={{
                                        backgroundColor: `${values.theme.secondaryTextColor}`,
                                        color: `${values.theme.secondaryColor}`,
                                      }}
                                    >
                                      <Typography variant="caption">Sample</Typography>
                                    </Box>
                                  </>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Enlistment */}

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>Enlistment</Box>
                        </Grid>

                        <Grid item xs={6}>
                          <TextField disabled={readonly} id="theme.preEnrollmentSupportPhone" name="theme.preEnrollmentSupportPhone" label="Support Phone Number" value={values.theme.preEnrollmentSupportPhone} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.preEnrollmentSupportPhone"))} helperText={getIn(errors, "theme.preEnrollmentSupportPhone")} />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField disabled={readonly} type={"email"} id="theme.preEnrollmentSupportEmail" name="theme.preEnrollmentSupportEmail" label="Support Email" value={values.theme.preEnrollmentSupportEmail} onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(errors, "theme.preEnrollmentSupportEmail"))} helperText={getIn(errors, "theme.preEnrollmentSupportEmail")} />
                        </Grid>

                        <Grid item xs={10} md={6}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Company Favicon
                            </InputLabel>
                            <Select native labelId="platform-vendor-id-label" id="theme.enlistmentFavicon" name="theme.enlistmentFavicon" value={values.theme.enlistmentFavicon} onChange={handleChange} label="Enlistment Favicon">
                              <option value={""}></option>
                              {values.publicPaths.map((path, index) => (
                                <option value={path} key={index}>
                                  {path}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={2} md={6}>
                          {values.theme.enlistmentFavicon !== "" ? (
                            <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                              <img src={`${appSetting.meshifyUrl}/api/files/public${values.theme.enlistmentFavicon}`} style={{ width: "60px", height: "auto" }} alt="Company Logo" />
                            </Box>
                          ) : null}
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                            <InputLabel id="platform-vendor-id-label" shrink={true}>
                              Enlistment Hero Banner
                            </InputLabel>
                            <Select native labelId="platform-vendor-id-label" id="theme.enlistmentMainPageBackgroundImage" name="theme.enlistmentMainPageBackgroundImage" value={values.theme.enlistmentMainPageBackgroundImage} onChange={handleChange} label="Enlistment Hero Banner">
                              <option value={""}></option>
                              {values.publicPaths.map((path, index) => (
                                <option value={path} key={index}>
                                  {path}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {values.theme.enlistmentMainPageBackgroundImage !== "" ? (
                            <Box display="flex" justifyContent="center" style={{ width: "100%" }}>
                              <img src={`${appSetting.meshifyUrl}/api/files/public${values.theme.enlistmentMainPageBackgroundImage}`} style={{ width: "auto", height: "100px" }} alt="Enlistment Hero Banner" />
                            </Box>
                          ) : null}
                        </Grid>

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={8}>
                                Warranties
                              </Grid>
                              <Grid item xs={4}>
                                <Box display="flex" justifyContent="flex-end" width={1}>
                                  {!readonly ? (
                                    <Box display="flex" justifyContent="flex-end" width={1}>
                                      <IconButton aria-controls="warranty-menu" aria-haspopup="true" onClick={handleAnchorElForWarrantyClick}>
                                        <AddIcon color="secondary" />
                                      </IconButton>
                                    </Box>
                                  ) : null}
                                  <Menu id="warranty-menu" anchorEl={anchorElForWarranty} keepMounted open={Boolean(anchorElForWarranty)} onClose={handleAnchorElForWarrantyClose}>
                                    <MenuItem
                                      onClick={(event) => {
                                        handleAddWarrantyClick("Water");
                                        handleAnchorElForWarrantyClose();
                                      }}
                                    >
                                      Water
                                    </MenuItem>
                                    <MenuItem
                                      onClick={(event) => {
                                        handleAddWarrantyClick("Freeze");
                                        handleAnchorElForWarrantyClose();
                                      }}
                                    >
                                      Freeze
                                    </MenuItem>
                                  </Menu>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={3}>
                            <FieldArray name="Warranties">
                              {({ insert, remove, push }) => (
                                <>
                                  {values.theme.warranties.length > 0 &&
                                    values.theme.warranties.map((warranty, index) => (
                                      <Grid item xs={12} sm={6} key={index}>
                                        <FormControl fullWidth={true}>
                                          <InputLabel shrink={true}>{warranty.warrantyType} Warranty Number</InputLabel>
                                          <Input
                                            required
                                            disabled={readonly}
                                            id={`warranties.${index}.warrantyNumber`}
                                            name={`warranties.${index}.warrantyNumber`}
                                            value={warranty.warrantyNumber}
                                            onChange={(event) => {
                                              handleChange(event);
                                              setFieldValue("theme.warranties[" + index + "].warrantyNumber", event.target.value);
                                            }}
                                            error={Boolean(getIn(errors, "theme.warranties[" + index + "].warrantyNumber"))}
                                            endAdornment={
                                              <InputAdornment position="end">
                                                {!readonly ? (
                                                  <React.Fragment>
                                                    <Tooltip arrow title="Remove">
                                                      <IconButton onClick={() => handleDeleteWarrantyClick(index)}>
                                                        <DeleteIcon color="error" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </React.Fragment>
                                                ) : null}
                                              </InputAdornment>
                                            }
                                          />
                                          <FormHelperText error={true}>{Boolean(getIn(errors, "theme.warranties[" + index + "].warrantyNumber")) ? <>{`${warranty.warrantyType} warranty number ${getIn(errors, "theme.warranties[" + index + "].warrantyNumber")}`}</> : null}</FormHelperText>
                                        </FormControl>
                                      </Grid>
                                    ))}
                                </>
                              )}
                            </FieldArray>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={8}>
                                Device Locations
                              </Grid>
                              <Grid item xs={4}>
                                <Box id="deviceLocations" display="flex" justifyContent="flex-end" width={1}>
                                  {!readonly && values.metadata.whiskerGroupId === "" ? (
                                    <Box display="flex" justifyContent="flex-end" width={1}>
                                      <IconButton
                                        onClick={() => {
                                          handleAddDeviceLocationClick();
                                        }}
                                      >
                                        <AddIcon color="secondary" />
                                      </IconButton>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={3}>
                            {values.theme.deviceLocations.map((value, index) => (
                              <Grid item key={index}>
                                {!readonly ? <Chip label={value} variant="outlined" className={pageStyles.chipRoot} color="default" deleteIcon={<DeleteIcon />} onDelete={() => handleDeleteSelectedDeviceLocationClick(value)} /> : <Chip label={value} variant="outlined" className={pageStyles.chipRoot} />}
                              </Grid>
                            ))}
                          </Grid>

                          <FormHelperText error>{getIn(errors, "theme.deviceLocations")}</FormHelperText>
                        </Grid>

                        <Grid item xs={12}>
                          <Box className={globalStyles.topic}>
                            <Grid container>
                              <Grid item xs={8}>
                                Notification Schemas
                              </Grid>
                              <Grid item xs={4}>
                                <Box id="defaultSchemas" display="flex" justifyContent="flex-end" width={1}>
                                  {!readonly && values.metadata.whiskerGroupId === "" ? (
                                    <Box display="flex" justifyContent="flex-end" width={1}>
                                      <IconButton
                                        onClick={() => {
                                          handleAddNotificationSchemaClick();
                                        }}
                                      >
                                        <AddIcon color="secondary" />
                                      </IconButton>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={3}>
                            {values.theme.defaultSchemas.map((value, index) => (
                              <Grid item key={index}>
                                {!readonly ? <Chip label={value} variant="outlined" className={pageStyles.chipRoot} color="default" deleteIcon={<DeleteIcon />} onDelete={() => handleDeleteSelectedDefaultSchemaClick(value)} /> : <Chip label={value} variant="outlined" className={pageStyles.chipRoot} />}
                              </Grid>
                            ))}
                          </Grid>

                          <FormHelperText error>{getIn(errors, "theme.defaultSchemas")}</FormHelperText>

                          <Box p={1}>&nbsp;</Box>
                          {missingNotificationSchemas.length > 0 ? (
                            <Alert severity="warning">
                              <Box>Missing notification schemas</Box>
                              <Box p={1}></Box>
                              <Grid container spacing={3}>
                                {missingNotificationSchemas.map((missingNotificationSchema, index) => (
                                  <Grid item key={index}>
                                    <Chip size="small" variant="outlined" className={pageStyles.chipRoot} label={missingNotificationSchema} />
                                  </Grid>
                                ))}
                              </Grid>
                            </Alert>
                          ) : null}
                        </Grid>

                        <Grid item xs={12}>
                          <Box p={9}>&nbsp;</Box>
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Box>
            </DialogContent>
            <DialogActions>
              {!readonly ? (
                <Button
                  disabled={!saveButtonVisible || saveInProgress}
                  onClick={() => {
                    formReference.current.handleSubmit();
                    setTimeout(() => {
                      checkOtherValidations();
                    }, 2000);
                  }}
                  color="secondary"
                  variant="contained"
                  className={globalStyles.buttonDefault}
                >
                  Update
                </Button>
              ) : null}

              <Button onClick={() => cancelProgramThemeAndMetadata()} color="secondary" autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </React.Fragment>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "400px",
    },
    colorTextBoxRoot: {
      "& .MuiInput-underline:before": {
        borderBottomWidth: 0,
        borderBottomColor: "transparent",
      },
    },
    chipRoot: {
      maxWidth: "300px",
      borderRadius: theme.spacing(1),
      "& .MuiChip-label": {
        paddingTop: "4px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      "& .MuiChip-deleteIcon": {
        color: theme.palette.error.main,
      },
    },
  })
);

export default ThemeAndMetadataCRUD;